/* eslint-disable react-hooks/exhaustive-deps -- lookup hydrators intentionally follow selected IDs */
import i18n from "../../i18n";
import { Trans } from "react-i18next";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./currentEmployment.css";
import { resolveApiUrl, resolveOrgApiUrl } from "../../api/api";
import { getAuthenticatedTenantId } from "../../lib/employee";
import sessionDB from "../../utils/sessionDB";
import {
  buildOnboardingAccessMessage,
  isOnboardingFeatureUnavailableError,
  isGenericOnboardingErrorMessage,
  parseOnboardingErrorResponse,
  handleOnboardingApiError,
  resolveOnboardingActionConfig
} from "./onboardingFeatureAccess";
import { format, parse, isBefore } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";


const ACTION_ORDER = ["add", "search", "update", "hierarchy"];
//const VISIBLE_ACTIONS = ["add", "update", "hierarchy"];
const ROLES_ENDPOINT = "/saas/hrms/org/roles";
const ROLE_BY_ID_ENDPOINT = "/saas/hrms/org/roles";
const CANONICAL_ROLES_ENDPOINT = "/saas/hrms/org/canonical_roles";
const DESIGNATIONS_ENDPOINT = "/saas/hrms/org/designations";
const DESIGNATION_BY_ID_ENDPOINT = "/saas/hrms/org/designations";
const DEPARTMENTS_ENDPOINT = "/saas/hrms/org/departments";
const DEPARTMENT_BY_ID_ENDPOINT = "/saas/hrms/org/departments";
const GRADE_LEVELS_ENDPOINT = "/saas/hrms/org/grade-levels";
const GRADE_LEVEL_BY_ID_ENDPOINT = "/saas/hrms/org/grade-levels";
const DIVISIONS_ENDPOINT = "/saas/hrms/org/divisions";
const DIVISION_BY_ID_ENDPOINT = "/saas/hrms/org/divisions";
const COST_CENTERS_ENDPOINT = "/saas/hrms/org/cost-centers";
const COST_CENTER_BY_ID_ENDPOINT = "/saas/hrms/org/cost-centers";
const SKILLS_ENDPOINT = "/saas/hrms/org/skills";
const SKILL_BY_ID_ENDPOINT = "/saas/hrms/org/skills";
const EMP_TYPES_ENDPOINT = "/saas/hrms/org/emp-type";
const EMP_STATUSES_ENDPOINT = "/saas/hrms/org/emp-status";

const ACTIONS = {
  add: {
    title: "Save Current Employment",
    serviceCode: "HR02A",
    endpoint: "/saas/hrms/current-employment/add",
    method: "POST"
  },
  search: {
    title: "Search",
    serviceCode: "HR02S",
    endpoint: "/saas/hrms/current-employment/search",
    method: "POST"
  },
  update: {
    title: "Update",
    serviceCode: "HR02U",
    endpoint: "/saas/hrms/current-employment/update/{empId}",
    method: "PUT"
  },
  hierarchy: {
    title: "Hierarchy",
    serviceCode: "HR02H",
    endpoint: "/saas/hrms/current-employment/hierarchy/{empId}",
    method: "GET"
  }
};

const EMP_TYPES = ["Probationary", "Permanent", "Contract", "Intern", "Consultant", "Notice_Period"];
const EMP_STATUSES = ["Active", "Inactive", "Terminated", "Onboarding"];
const LOCATIONS = [
  { value: "1", labelKey: "onboarding.options.workLocation.chennai" },
  { value: "2", labelKey: "onboarding.options.workLocation.bengaluru" },
  { value: "3", labelKey: "onboarding.options.workLocation.mumbai" },
  { value: "4", labelKey: "onboarding.options.workLocation.remote" }
];

const EMP_TYPE_OPTION_KEYS = {
  Probationary: "onboarding.options.empType.probationary",
  Permanent: "onboarding.options.empType.permanent",
  Contract: "onboarding.options.empType.contract",
  Intern: "onboarding.options.empType.intern",
  Consultant: "onboarding.options.empType.consultant",
  Notice_Period: "onboarding.options.empType.noticePeriod"
};

const EMP_STATUS_OPTION_KEYS = {
  Active: "onboarding.options.empStatus.active",
  Inactive: "onboarding.options.empStatus.inactive",
  Terminated: "onboarding.options.empStatus.terminated",
  Onboarding: "onboarding.options.empStatus.onboarding"
};

const FIELD_LABEL_KEYS = {
  empId: "common.employeeId",
  empType: "onboarding.employeeType",
  empStatus: "onboarding.labels.employeeStatus",
  canonicalRoleName: "onboarding.labels.canonicalRoleName",
  designationId: "onboarding.labels.designation",
  departmentId: "common.department",
  gradeLevelId: "onboarding.labels.gradeLevel",
  divisionId: "onboarding.labels.division",
  costCenterId: "onboarding.labels.costCenter",
  workLocationId: "onboarding.labels.workLocation",
  dateOfJoin: "onboarding.labels.dateOfJoining",
  terminationDate: "onboarding.labels.terminationDate",
  managerId: "common.manager",
  hrId: "onboarding.labels.hr",
  skillId: "onboarding.labels.skills",
  roleId: "onboarding.labels.role"
};

const LOOKUP_ERROR_KEYS = {
  roles: "onboarding.errors.unableToLoadRoles",
  canonicalRoles: "onboarding.errors.unableToLoadCanonicalRoles",
  skills: "onboarding.errors.unableToLoadSkills",
  gradeLevels: "onboarding.errors.unableToLoadGradeLevels",
  divisions: "onboarding.errors.unableToLoadDivisions",
  costCenters: "onboarding.errors.unableToLoadCostCenters",
  designations: "onboarding.errors.unableToLoadDesignations",
  departments: "onboarding.errors.unableToLoadDepartments"
};

function getFieldLabel(name) {
  const key = FIELD_LABEL_KEYS[name];
  return key ? i18n.t(key) : name;
}

function getFieldLabelsMap() {
  return Object.fromEntries(
    Object.keys(FIELD_LABEL_KEYS).map((name) => [name, getFieldLabel(name)])
  );
}

function getRuleMessage(errorKey, fieldName) {
  const fullKey = `onboarding.errors.${errorKey}`;
  return i18n.exists(fullKey)
    ? i18n.t(fullKey)
    : i18n.t("validation.fieldRequired", { field: getFieldLabel(fieldName) });
}

function translateEmpTypeLabel(value) {
  const key = EMP_TYPE_OPTION_KEYS[value];
  return key ? i18n.t(key) : value;
}

function translateEmpStatusLabel(value) {
  const key = EMP_STATUS_OPTION_KEYS[value];
  return key ? i18n.t(key) : value;
}

function translateEmpTypeOptions(options) {
  return options.map((option) => ({
    ...option,
    label: translateEmpTypeLabel(option.value)
  }));
}

function translateEmpStatusOptions(options) {
  return options.map((option) => ({
    ...option,
    label: translateEmpStatusLabel(option.value)
  }));
}

function getLookupErrorMessage(key) {
  const messageKey = LOOKUP_ERROR_KEYS[key];
  return messageKey ? i18n.t(messageKey) : i18n.t("onboarding.errors.currentEmploymentRequestFailed");
}

const INITIAL_FORM = {
  empId: "",
  empType: "",
  empStatus: "",
  roleId: "",
  canonicalRoleName: "",
  skillId: "",
  designationId: "",
  departmentId: "",
  gradeLevelId: "",
  divisionId: "",
  costCenterId: "",
  workLocationId: "",
  loginBasedOnGps: "N",
  managerId: "",
  hrId: "",
  dateOfJoin: "",
  terminationDate: ""
};

/*function getPersistedCurrentEmploymentDraft() {
  const draft = sessionDB.getSession().onboardingCurrentEmploymentDraft;
  return draft && typeof draft === "object" ? draft : {};
}*/

const REQUIRED_BY_ACTION = {
  add: ["empId", "empType", "empStatus", "canonicalRoleName", "designationId", "departmentId", "gradeLevelId", "divisionId", "costCenterId", "workLocationId", "dateOfJoin"],
  update: ["empId", "empType", "empStatus", "canonicalRoleName", "designationId", "departmentId", "gradeLevelId", "divisionId", "costCenterId", "workLocationId", "dateOfJoin"],
  search: [],
  hierarchy: ["empId"]
};

const RULES = {
  empId: (value) => (!value.trim() ? getRuleMessage("empIdRequired", "empId") : !/^\d+$/.test(value.trim()) ? getRuleMessage("empIdMustBeNumeric", "empId") : ""),
  // Employee type/status options are now loaded from /org/emp-type/ and
  // /org/emp-status/, so the valid set is dynamic. Presence (required) is
  // enforced separately; no static membership check here.
  empType: () => "",
  empStatus: () => "",
  canonicalRoleName: (value) => (!value ? "" : value.trim().length > 100 ? i18n.t("onboarding.errors.max100Characters") : ""),
  skillId: (value) => (!value ? "" : value.trim().length > 150 ? i18n.t("onboarding.errors.max150Characters") : ""),
  designationId: (value) => (!value ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("chooseValidDesignation", "designationId") : ""),
  departmentId: (value) => (!value ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("chooseValidDepartment", "departmentId") : ""),
  gradeLevelId: (value) => (!value ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("chooseValidGradeLevel", "gradeLevelId") : ""),
  divisionId: (value) => (!value ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("chooseValidDivision", "divisionId") : ""),
  costCenterId: (value) => (!value ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("chooseValidCostCenter", "costCenterId") : ""),
  workLocationId: (value) => (!value ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("chooseValidLocation", "workLocationId") : ""),
  managerId: (value) => (!value ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("managerMustBeNumeric", "managerId") : ""),
  hrId: (value) => (!value ? "" : !/^\d+$/.test(value.trim()) ? getRuleMessage("hrMustBeNumeric", "hrId") : ""),
  dateOfJoin: (value) => (!value ? "" : !/^\d{2}-\d{2}-\d{4}$/.test(value.trim()) ? getRuleMessage("useDdMmYyyy", "dateOfJoin") : ""),
  terminationDate: (value, allValues) => {
    if (!value) return "";
    if (!/^\d{2}-\d{2}-\d{4}$/.test(value.trim())) return getRuleMessage("useDdMmYyyy", "terminationDate");
    const joinValue = allValues && allValues.dateOfJoin;
    if (joinValue && /^\d{2}-\d{2}-\d{4}$/.test(joinValue.trim())) {
      const termination = parse(value.trim(), "dd-MM-yyyy", new Date());
      const joining = parse(joinValue.trim(), "dd-MM-yyyy", new Date());
      if (isBefore(termination, joining)) return getRuleMessage("terminationBeforeJoining", "terminationDate");
    }
    return "";
  }
};

function buildUrl(template, form) {
  return template.replace("{empId}", encodeURIComponent(form.empId.trim()));
}

function getNextAction(currentAction) {
  const currentIndex = ACTION_ORDER.indexOf(currentAction);
  return ACTION_ORDER[(currentIndex + 1) % ACTION_ORDER.length];
}

function formatDisplayDateInput(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
}

function displayDateToApiDate(value) {
  if (!/^\d{2}-\d{2}-\d{4}$/.test(String(value || "").trim())) return undefined;
  const [day, month, year] = value.trim().split("-");
  return `${year}-${month}-${day}`;
}

function normalizeEmploymentErrorMessage(message) {
  const cleaned = String(message || "").trim();
  if (!cleaned) return i18n.t("onboarding.errors.currentEmploymentRequestFailed");

  return cleaned
    .replace(/currentent/gi, "current")
    .replace(/employement/gi, "employment")
    .replace(/Validation Failed\s*-\s*current employment/i, i18n.t("onboarding.errors.currentEmploymentRequestFailed"));
}

/* eslint-disable-next-line no-unused-vars -- retained for normalized API error handling */
function extractEmploymentErrorMessage(body, fallback) {
  if (!body || typeof body !== "object") {
    return normalizeEmploymentErrorMessage(fallback);
  }

  const direct = [body.message, body.error, body.data]
    .find((value) => typeof value === "string" && value.trim());
  if (direct) {
    return normalizeEmploymentErrorMessage(direct);
  }

  const objectCollections = [body.data, body.errors, body.validationErrors, body.fieldErrors];
  for (const collection of objectCollections) {
    if (!collection || Array.isArray(collection) || typeof collection !== "object") {
      continue;
    }

    const parts = Object.entries(collection)
      .map(([field, message]) => {
        if (typeof message === "string" && message.trim()) {
          return `${field}: ${message.trim()}`;
        }
        if (Array.isArray(message) && message.length > 0) {
          return `${field}: ${message.map((item) => String(item || "").trim()).filter(Boolean).join(", ")}`;
        }
        return "";
      })
      .filter(Boolean);

    if (parts.length) {
      return normalizeEmploymentErrorMessage(parts.join(" | "));
    }
  }

  const arrayCollections = [body.details, body.errors, body.validationErrors, body.fieldErrors, body.data?.errors];
  for (const collection of arrayCollections) {
    if (!Array.isArray(collection) || collection.length === 0) {
      continue;
    }

    const parts = collection
      .map((entry) => {
        if (typeof entry === "string" && entry.trim()) {
          return entry.trim();
        }
        if (entry && typeof entry === "object") {
          return (
            entry.message ||
            entry.defaultMessage ||
            entry.error ||
            [entry.field, entry.reason].filter(Boolean).join(": ") ||
            ""
          );
        }
        return "";
      })
      .filter(Boolean);

    if (parts.length) {
      return normalizeEmploymentErrorMessage(parts.join(" | "));
    }
  }

  return normalizeEmploymentErrorMessage(fallback);
}

function normalizeLookupOptions(payload, idKeys = [], labelKeys = []) {
  const candidates = [
    payload,
    payload?.data,
    payload?.content,
    payload?.result,
    payload?.data?.content,
    payload?.result?.content,
    payload?.data?.result,
    payload?.data?.result?.content,
    payload?.records,
    payload?.items,
    payload?.rows,
    payload?.roles,
    payload?.designations,
    payload?.departments,
    payload?.gradeLevels,
    payload?.divisions,
    payload?.costCenters,
    payload?.skills,
    payload?.data?.roles,
    payload?.data?.designations,
    payload?.data?.departments,
    payload?.data?.gradeLevels,
    payload?.data?.divisions,
    payload?.data?.costCenters,
    payload?.data?.skills,
    payload?.data?.records,
    payload?.data?.items,
    payload?.data?.rows
  ];

  const list = candidates.find((value) => Array.isArray(value));
  if (!Array.isArray(list)) return [];

  return list
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const id = [
        ...idKeys.map((key) => item?.[key]),
        item?.role_id,
        item?.designation_id,
        item?.department_id,
        item?.grade_level_id,
        item?.division_id,
        item?.cost_center_id,
        item?.skill_id
      ].find((value) => value !== null && value !== undefined && String(value).trim());
      const label = [
        ...labelKeys.map((key) => item?.[key]),
        item?.role_name,
        item?.designation_name,
        item?.department_name,
        item?.grade_level_name,
        item?.division_name,
        item?.cost_center_name,
        item?.skill_name,
        item?.description,
        item?.title
      ].find((value) => value !== null && value !== undefined && String(value).trim());
      if (!id || !label) return null;
      return { value: String(id).trim(), label: String(label).trim() };
    })
    .filter(Boolean);
}

// Enum-style lookups (emp type / emp status) may come back either as a plain
// array of strings (["Permanent", ...]) or as objects. Unlike the id-based
// org lookups, the VALUE we send back to the API is the enum string itself,
// so value and label both resolve to that string (label prefers a nicer
// description when present). Falls back gracefully to [] on unexpected shapes.
function normalizeEnumOptions(payload, valueKeys = [], labelKeys = []) {
  const candidates = [
    payload,
    payload?.data,
    payload?.content,
    payload?.result,
    payload?.data?.content,
    payload?.records,
    payload?.items,
    payload?.rows,
    payload?.data?.records
  ];
  const list = candidates.find((value) => Array.isArray(value));
  if (!Array.isArray(list)) return [];

  return list
    .map((item) => {
      if (typeof item === "string") {
        const str = item.trim();
        return str ? { value: str, label: str } : null;
      }
      if (!item || typeof item !== "object") return null;
      const value = [
        ...valueKeys.map((key) => item?.[key]),
        item?.name,
        item?.code,
        item?.value,
        item?.id
      ].find((entry) => entry !== null && entry !== undefined && String(entry).trim());
      if (value === null || value === undefined || !String(value).trim()) return null;
      const label = [
        ...labelKeys.map((key) => item?.[key]),
        item?.description,
        item?.label,
        item?.name,
        value
      ].find((entry) => entry !== null && entry !== undefined && String(entry).trim());
      return { value: String(value).trim(), label: String(label ?? value).trim() };
    })
    .filter(Boolean);
}

function extractLookupLabel(payload, labelKeys = []) {
  if (!payload || typeof payload !== "object") return "";
  const source = Array.isArray(payload) ? payload[0] : payload;
  const value = [
    ...labelKeys.map((key) => source?.[key]),
    source?.role_name,
    source?.designation_name,
    source?.department_name,
    source?.grade_level_name,
    source?.division_name,
    source?.cost_center_name,
    source?.skill_name,
    source?.description,
    source?.title
  ].find((entry) => entry !== null && entry !== undefined && String(entry).trim());
  return value ? String(value).trim() : "";
}

function parseMultiValueIds(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function Field({ id, label, required, error, hint, children, span2 = false }) {
  return (
    <div className={`cem-field${span2 ? " span2" : ""}`}>
      <label className={`cem-label${required ? " required" : ""}`} htmlFor={id}>{label}</label>
      {children}
      {hint ? <p className="cem-hint">{hint}</p> : null}
      <p className="cem-error" role="alert">{error || ""}</p>
    </div>
  );
}

export default function CurrentEmployment({
  accessToken,
  tenantCode,
  onBack,
  onNext,
  onSuccess,
  breadcrumb,
  initialEmployeeId = "",
  isTabMode = false,
  isActive = true,
  saveTriggerRef
}) {
  useEffect(() => {
    if (saveTriggerRef) {
      saveTriggerRef.current = {
        submit: () => {
          if (action === "search") {
            runSearch();
          } else if (action === "hierarchy") {
            runHierarchy();
          } else {
            submitMutation();
          }
        }
      };
    }
  });
  const [action, setAction] = useState("add");
  const [form, setForm] = useState(() => ({
    ...INITIAL_FORM,
    empId: String(initialEmployeeId || "")
  }));
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [banner, _setBanner] = useState(null);
  const setBanner = (val) => {
    _setBanner((prev) => {
      const next = typeof val === "function" ? val(prev) : val;
      if (next && next.type === "error") {
        if (isOnboardingFeatureUnavailableError(next.msg)) {
          return { type: "error", msg: i18n.t("onboarding.featureUnavailable") };
        }
        if (isGenericOnboardingErrorMessage(next.msg)) {
          return prev;
        }
      }
      return next;
    });
  };
  const [roleOptions, setRoleOptions] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [canonicalRoleOptions, setCanonicalRoleOptions] = useState([]);
  const [canonicalRolesLoading, setCanonicalRolesLoading] = useState(false);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [designationsLoading, setDesignationsLoading] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [gradeLevelOptions, setGradeLevelOptions] = useState([]);
  const [gradeLevelsLoading, setGradeLevelsLoading] = useState(false);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [divisionsLoading, setDivisionsLoading] = useState(false);
  const [costCenterOptions, setCostCenterOptions] = useState([]);
  const [costCentersLoading, setCostCentersLoading] = useState(false);
  const [skillOptions, setSkillOptions] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [empTypeOptions, setEmpTypeOptions] = useState([]);
  const [empTypesLoading, setEmpTypesLoading] = useState(false);
  const [empStatusOptions, setEmpStatusOptions] = useState([]);
  const [empStatusesLoading, setEmpStatusesLoading] = useState(false);
  const lookupsLoadedRef = useRef({
    roles: false,
    canonicalRoles: false,
    skills: false,
    gradeLevels: false,
    divisions: false,
    costCenters: false,
    designations: false,
    departments: false,
    empTypes: false,
    empStatuses: false
  });
  const employmentLoadedForIdRef = useRef("");
  const requiredFields = useMemo(() => new Set(REQUIRED_BY_ACTION[action] || []), [action]);
  const currentAction = useMemo(() => resolveOnboardingActionConfig({
    submoduleNames: ["Current Employment"],
    action,
    fallback: ACTIONS[action]
  }), [action]);
  const tenantId = sessionDB.getTenantId() || getAuthenticatedTenantId() || "";
  const tenantCodeFromSession = sessionDB.getTenantCode() || tenantCode || "";
  const sessionAccessToken = sessionDB.getAccessToken() || accessToken || "";
  const selectedRoleIds = useMemo(() => parseMultiValueIds(form.roleId), [form.roleId]);
  const selectedSkillIds = useMemo(() => parseMultiValueIds(form.skillId), [form.skillId]);
  const selectedCanonicalRoleNames = useMemo(() => parseMultiValueIds(form.canonicalRoleName), [form.canonicalRoleName]);

  const fetchEmployment = useCallback(async () => {
    if (!initialEmployeeId) return;
    try {
      console.log(initialEmployeeId)
      const response = await fetch(resolveApiUrl(`/saas/hrms/current-employment/search/${initialEmployeeId}`), {
        method: 'GET',
        headers: {
          'x-tenant-code': tenantCodeFromSession || "",
          'x-tenant-id': String(tenantId || ""),
          'x-service-code': 'HR02S',
          authorization: `Bearer ${sessionAccessToken}`
        }
      });
     
      if (response.ok) {
        const body = await response.json();
        console.log("sghd:",body)
        const data = body.data || body;
        if (data) {
          const rawSkills = data.skillId ?? data.skills ?? data.skillIds ?? "";
          const parsedSkillId = Array.isArray(rawSkills) ? rawSkills.join(",") : String(rawSkills || "");

          const rawGps = data.loginBasedOnGps ?? data.gpsLogin ?? data.isGpsLogin ?? data.gps_login ?? data.gpsLoginEnabled;
          const parsedGps = (rawGps === true || rawGps === "Y" || rawGps === "true" || rawGps === 1) ? "Y" : "N";

          const parsedRole = data.canonicalRoleName || data.roleName || data.canonical_role_name || "";

          setForm({
            empId: String(initialEmployeeId),
            empType: data.empType || "",
            empStatus: data.empStatus || "",
            dateOfJoin: data.dateOfJoin ? format(new Date(data.dateOfJoin), "dd-MM-yyyy") : "",
            probationPeriod: String(data.probationPeriod || ""),
            dateOfConfirmation: data.dateOfConfirmation ? format(new Date(data.dateOfConfirmation), "dd-MM-yyyy") : "",
            roleId: String(data.roleId || data.role_id || data.orgRoleId || ""),
            canonicalRoleName: parsedRole,
            skillId: parsedSkillId,
            designationId: String(data.designationId || data.designation_id || data.orgDesignationId || ""),
            departmentId: String(data.departmentId || data.department_id || data.orgDepartmentId || ""),
            gradeLevelId: String(data.gradeLevelId || data.grade_level_id || data.gradeId || ""),
            divisionId: String(data.divisionId || data.division_id || ""),
            costCenterId: String(data.costCenterId || data.cost_center_id || ""),
            workLocationId: String(data.workLocationId || data.work_location_id || data.locationId || ""),
            loginBasedOnGps: parsedGps,
            managerId: String(data.managerId || data.manager_id || ""),
            hrId: String(data.hrId || data.hr_id || ""),
            noticePeriod: String(data.noticePeriod || ""),
            isTerminated: data.isTerminated || false,
            terminationDate: data.terminationDate ? format(new Date(data.terminationDate), "dd-MM-yyyy") : ""
          });
          setAction("update");
        }
      }
    } catch (err) {
      console.error("Failed to load current employment info", err);
    }
  }, [initialEmployeeId, tenantCodeFromSession, tenantId, sessionAccessToken]);

  useEffect(() => {
    if (!initialEmployeeId) {
      setForm({
        ...INITIAL_FORM,
        empId: ""
      });
      setAction("add");
      return;
    }
    setForm((prev) => ({ ...prev, empId: String(initialEmployeeId) }));
  }, [initialEmployeeId]);

  useEffect(() => {
    if (!isActive || !initialEmployeeId) return;
    const empKey = String(initialEmployeeId);
    if (employmentLoadedForIdRef.current === empKey) return;
    employmentLoadedForIdRef.current = empKey;
    fetchEmployment();
  }, [isActive, initialEmployeeId, fetchEmployment]);

  async function fetchOrgLookup(path) {
    if (!tenantId) {
      throw new Error("tenant missing");
    }

    const nextTenantId = String(tenantId || "").trim();
    const nextTenantCode = String(tenantCodeFromSession || "").trim();
    const nextAccessToken = String(sessionAccessToken || "").trim();
    const requestPath = `${path}${path.includes("?") ? "&" : "?"}tenantId=${encodeURIComponent(nextTenantId)}`;
    const headers = {
      Accept: "*/*",
      "x-service-code": "HR20A",
      ...(nextAccessToken ? { Authorization: `Bearer ${nextAccessToken}` } : {}),
      ...(nextTenantId ? { "X-Tenant-Id": nextTenantId } : {}),
      ...(nextTenantCode ? { "X-Tenant-Code": nextTenantCode } : {})
    };

    try {
      const response = await fetch(resolveOrgApiUrl(requestPath), {
        method: "GET",
        headers
      });

      if (!response.ok) {
        throw new Error(String(response.status || "request failed"));
      }

      return await response.json().catch(() => []);
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("request failed");
    }
  }

  async function fetchOrgLookupById(pathTemplate, idValue) {
    if (!tenantId) {
      return null;
    }

    const nextTenantId = String(tenantId || "").trim();
    const nextTenantCode = String(tenantCodeFromSession || "").trim();
    const nextAccessToken = String(sessionAccessToken || "").trim();
    const requestPath = `${pathTemplate}/${encodeURIComponent(String(idValue))}?tenantId=${encodeURIComponent(nextTenantId)}`;
    const headers = {
      Accept: "*/*",
      "x-service-code": "HR20A",
      ...(nextAccessToken ? { Authorization: `Bearer ${nextAccessToken}` } : {}),
      ...(nextTenantId ? { "X-Tenant-Id": nextTenantId } : {}),
      ...(nextTenantCode ? { "X-Tenant-Code": nextTenantCode } : {})
    };

    try {
      const response = await fetch(resolveOrgApiUrl(requestPath), {
        method: "GET",
        headers
      });

      if (!response.ok) {
        return null;
      }

      return await response.json().catch(() => ({}));
    } catch {
      return null;
    }
  }



  useEffect(() => {
    // Parent keeps Current Employment mounted while hidden in tab mode.
    // Load org lookups only when this tab/page is active.
    if (!isActive || !tenantId) return;

    let cancelled = false;
    const loaded = lookupsLoadedRef.current;

    async function loadLookup({
      key,
      endpoint,
      setOptions,
      setLoading,
      mapOptions,
      onError,
      cacheOnError = false
    }) {
      if (loaded[key]) return;
      loaded[key] = "loading";
      setLoading(true);
      try {
        const data = await fetchOrgLookup(endpoint);
        if (cancelled) {
          loaded[key] = false;
          return;
        }
        setOptions(mapOptions(data));
        loaded[key] = true;
      } catch (error) {
        if (cancelled) {
          loaded[key] = false;
          return;
        }
        if (onError) {
          onError(error);
        } else {
          setOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || getLookupErrorMessage(key)
          });
        }
        loaded[key] = cacheOnError ? true : false;
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    Promise.all([
      loadLookup({
        key: "roles",
        endpoint: ROLES_ENDPOINT,
        setOptions: setRoleOptions,
        setLoading: setRolesLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["roleId", "id", "value"], ["roleName", "name", "label", "value"]),
        onError: (error) => {
          setRoleOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: `${i18n.t("onboarding.errors.unableToLoadRoles")} (${error.message || "request failed"}).`
          });
        }
      }),
      loadLookup({
        key: "canonicalRoles",
        endpoint: CANONICAL_ROLES_ENDPOINT,
        setOptions: setCanonicalRoleOptions,
        setLoading: setCanonicalRolesLoading,
        mapOptions: (data) => normalizeLookupOptions(
          data,
          ["canonicalRoleId", "roleId", "id", "value"],
          ["canonicalRoleName", "roleName", "name", "label", "value"]
        ),
        onError: (error) => {
          setCanonicalRoleOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || i18n.t("onboarding.errors.unableToLoadCanonicalRoles")
          });
        }
      }),
      loadLookup({
        key: "skills",
        endpoint: SKILLS_ENDPOINT,
        setOptions: setSkillOptions,
        setLoading: setSkillsLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["skillId", "id", "value"], ["skillName", "name", "label", "value"]),
        onError: (error) => {
          setSkillOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || i18n.t("onboarding.errors.unableToLoadSkills")
          });
        }
      }),
      loadLookup({
        key: "gradeLevels",
        endpoint: GRADE_LEVELS_ENDPOINT,
        setOptions: setGradeLevelOptions,
        setLoading: setGradeLevelsLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["gradeLevelId", "id", "value"], ["gradeLevelName", "name", "label", "value"]),
        onError: (error) => {
          setGradeLevelOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || i18n.t("onboarding.errors.unableToLoadGradeLevels")
          });
        }
      }),
      loadLookup({
        key: "divisions",
        endpoint: DIVISIONS_ENDPOINT,
        setOptions: setDivisionOptions,
        setLoading: setDivisionsLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["divisionId", "id", "value"], ["divisionName", "name", "label", "value"]),
        onError: (error) => {
          setDivisionOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || i18n.t("onboarding.errors.unableToLoadDivisions")
          });
        }
      }),
      loadLookup({
        key: "costCenters",
        endpoint: COST_CENTERS_ENDPOINT,
        setOptions: setCostCenterOptions,
        setLoading: setCostCentersLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["costCenterId", "id", "value"], ["costCenterName", "name", "label", "value"]),
        onError: (error) => {
          setCostCenterOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || i18n.t("onboarding.errors.unableToLoadCostCenters")
          });
        }
      }),
      loadLookup({
        key: "designations",
        endpoint: DESIGNATIONS_ENDPOINT,
        setOptions: setDesignationOptions,
        setLoading: setDesignationsLoading,
        mapOptions: (data) => normalizeLookupOptions(data, ["designationId", "id", "value"], ["designationName", "name", "label", "value"]),
        onError: (error) => {
          setDesignationOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || i18n.t("onboarding.errors.unableToLoadDesignations")
          });
        }
      }),
      loadLookup({
        key: "departments",
        endpoint: DEPARTMENTS_ENDPOINT,
        setOptions: setDepartmentOptions,
        setLoading: setDepartmentsLoading,
        mapOptions: (data) => normalizeLookupOptions(
          data,
          ["departmentId", "deptId", "id", "value"],
          ["departmentName", "deptName", "name", "label", "value"]
        ),
        onError: (error) => {
          setDepartmentOptions([]);
          setBanner((current) => current || {
            type: "error",
            msg: error.message || i18n.t("onboarding.errors.unableToLoadDepartments")
          });
        }
      }),
      loadLookup({
        key: "empTypes",
        endpoint: EMP_TYPES_ENDPOINT,
        setOptions: setEmpTypeOptions,
        setLoading: setEmpTypesLoading,
        mapOptions: (data) => {
          // /org/emp-type/ returns { typeId, type_Name, ... }. empType is stored
          // by name (not id) in current-employment, so value === label === type_Name.
          const options = normalizeEnumOptions(data, ["type_Name", "typeName"], ["type_Name", "typeName"]);
          return options.length ? translateEmpTypeOptions(options) : translateEmpTypeOptions(EMP_TYPES.map((value) => ({ value, label: value })));
        },
        onError: () => {
          setEmpTypeOptions(translateEmpTypeOptions(EMP_TYPES.map((value) => ({ value, label: value }))));
        },
        cacheOnError: true
      }),
      loadLookup({
        key: "empStatuses",
        endpoint: EMP_STATUSES_ENDPOINT,
        setOptions: setEmpStatusOptions,
        setLoading: setEmpStatusesLoading,
        mapOptions: (data) => {
          // /org/emp-status/ returns { statusId, status_Name, ... }. empStatus is
          // stored by name (not id), so value === label === status_Name.
          const options = normalizeEnumOptions(data, ["status_Name", "statusName"], ["status_Name", "statusName"]);
          return options.length ? translateEmpStatusOptions(options) : translateEmpStatusOptions(EMP_STATUSES.map((value) => ({ value, label: value })));
        },
        onError: () => {
          setEmpStatusOptions(translateEmpStatusOptions(EMP_STATUSES.map((value) => ({ value, label: value }))));
        },
        cacheOnError: true
      })
    ]);

    return () => {
      cancelled = true;
    };
  }, [isActive, tenantId]);

  function resetFeedback() {
    setBanner(null);
  }

  function validateField(name, value, touchMap = touched) {
    const requiredMessage = requiredFields.has(name) && !String(value || "").trim() ? i18n.t("validation.fieldRequired", { field: getFieldLabel(name) }) : "";
    const ruleMessage = RULES[name] ? RULES[name](value || "", { ...form, [name]: value }) : "";
    const message = requiredMessage || ruleMessage;
    setErrors((prev) => ({ ...prev, [name]: touchMap[name] ? message : "" }));
    return !message;
  }

  function validateAll() {
    const nextTouched = {};
    const nextErrors = {};
    let valid = true;

    Object.keys(RULES).forEach((name) => {
      nextTouched[name] = true;
      const requiredMessage = requiredFields.has(name) && !String(form[name] || "").trim() ? i18n.t("validation.fieldRequired", { field: getFieldLabel(name) }) : "";
      const ruleMessage = RULES[name] ? RULES[name](form[name] || "", form) : "";
      const message = requiredMessage || ruleMessage;
      nextErrors[name] = message;
      if (message) valid = false;
    });

    setTouched(nextTouched);
    setErrors(nextErrors);
    return valid;
  }

  function handleActionChange(nextAction) {
    setAction(nextAction);
    setErrors({});
    setTouched({});
    resetFeedback();
  }

  function handleInputChange(event) {
    const { name, value } = event.target;
    const nextValue = name === "dateOfJoin" || name === "terminationDate" ? formatDisplayDateInput(value) : value;
    setForm((prev) => ({
      ...prev,
      [name]: nextValue
    }));
    if (touched[name]) validateField(name, nextValue);
  }

  function handleInputBlur(event) {
    const { name, value } = event.target;
    const nextTouched = { ...touched, [name]: true };
    setTouched(nextTouched);
    validateField(name, value, nextTouched);
  }

  async function hydrateRoleName(roleId) {
    if (!tenantId || !roleId || roleOptions.some((option) => option.value === String(roleId))) return;

    try {
      const data = await fetchOrgLookupById(ROLE_BY_ID_ENDPOINT, roleId);
      if (!data) return;
      const roleLabel = extractLookupLabel(data, ["roleName", "name", "label", "value"]);
      if (!roleLabel) return;
      setRoleOptions((current) => current.some((option) => option.value === String(roleId))
        ? current
        : [...current, { value: String(roleId), label: roleLabel }]);
    } catch {}
  }

  async function hydrateDesignationOption(designationId) {
    if (!tenantId || !designationId || designationOptions.some((option) => option.value === String(designationId))) return;

    try {
      const data = await fetchOrgLookupById(DESIGNATION_BY_ID_ENDPOINT, designationId);
      if (!data) return;
      const label = extractLookupLabel(data, ["designationName", "name", "label", "value"]);
      if (!label) return;
      setDesignationOptions((current) => current.some((option) => option.value === String(designationId))
        ? current
        : [...current, { value: String(designationId), label }]);
    } catch {}
  }

  async function hydrateDepartmentOption(departmentId) {
    if (!tenantId || !departmentId || departmentOptions.some((option) => option.value === String(departmentId))) return;

    try {
      const data = await fetchOrgLookupById(DEPARTMENT_BY_ID_ENDPOINT, departmentId);
      if (!data) return;
      const label = extractLookupLabel(data, ["departmentName", "deptName", "name", "label", "value"]);
      if (!label) return;
      setDepartmentOptions((current) => current.some((option) => option.value === String(departmentId))
        ? current
        : [...current, { value: String(departmentId), label }]);
    } catch {}
  }

  async function hydrateGradeLevelOption(gradeLevelId) {
    if (!tenantId || !gradeLevelId || gradeLevelOptions.some((option) => option.value === String(gradeLevelId))) return;

    try {
      const data = await fetchOrgLookupById(GRADE_LEVEL_BY_ID_ENDPOINT, gradeLevelId);
      if (!data) return;
      const label = extractLookupLabel(data, ["gradeLevelName", "name", "label", "value"]);
      if (!label) return;
      setGradeLevelOptions((current) => current.some((option) => option.value === String(gradeLevelId))
        ? current
        : [...current, { value: String(gradeLevelId), label }]);
    } catch {}
  }

  async function hydrateDivisionOption(divisionId) {
    if (!tenantId || !divisionId || divisionOptions.some((option) => option.value === String(divisionId))) return;

    try {
      const data = await fetchOrgLookupById(DIVISION_BY_ID_ENDPOINT, divisionId);
      if (!data) return;
      const label = extractLookupLabel(data, ["divisionName", "name", "label", "value"]);
      if (!label) return;
      setDivisionOptions((current) => current.some((option) => option.value === String(divisionId))
        ? current
        : [...current, { value: String(divisionId), label }]);
    } catch {}
  }

  async function hydrateCostCenterOption(costCenterId) {
    if (!tenantId || !costCenterId || costCenterOptions.some((option) => option.value === String(costCenterId))) return;

    try {
      const data = await fetchOrgLookupById(COST_CENTER_BY_ID_ENDPOINT, costCenterId);
      if (!data) return;
      const label = extractLookupLabel(data, ["costCenterName", "name", "label", "value"]);
      if (!label) return;
      setCostCenterOptions((current) => current.some((option) => option.value === String(costCenterId))
        ? current
        : [...current, { value: String(costCenterId), label }]);
    } catch {}
  }

  async function hydrateSkillOption(skillId) {
    if (!tenantId || !skillId || skillOptions.some((option) => option.value === String(skillId))) return;

    try {
      const data = await fetchOrgLookupById(SKILL_BY_ID_ENDPOINT, skillId);
      if (!data) return;
      const label = extractLookupLabel(data, ["skillName", "name", "label", "value"]);
      if (!label) return;
      setSkillOptions((current) => current.some((option) => option.value === String(skillId))
        ? current
        : [...current, { value: String(skillId), label }]);
    } catch {}
  }

  useEffect(() => {
    if (!isActive || selectedRoleIds.length === 0) return;
    selectedRoleIds.forEach((roleId) => {
      if (!roleOptions.some((option) => option.value === roleId)) {
        hydrateRoleName(roleId);
      }
    });
  }, [isActive, selectedRoleIds, roleOptions]);

  useEffect(() => {
    if (!isActive || !form.designationId) return;
    if (designationOptions.some((option) => option.value === form.designationId)) return;
    hydrateDesignationOption(form.designationId);
  }, [isActive, designationOptions, form.designationId]);

  useEffect(() => {
    if (!isActive || !form.departmentId) return;
    if (departmentOptions.some((option) => option.value === form.departmentId)) return;
    hydrateDepartmentOption(form.departmentId);
  }, [isActive, departmentOptions, form.departmentId]);

  useEffect(() => {
    if (!isActive || !form.gradeLevelId) return;
    if (gradeLevelOptions.some((option) => option.value === form.gradeLevelId)) return;
    hydrateGradeLevelOption(form.gradeLevelId);
  }, [isActive, form.gradeLevelId, gradeLevelOptions]);

  useEffect(() => {
    if (!isActive || !form.divisionId) return;
    if (divisionOptions.some((option) => option.value === form.divisionId)) return;
    hydrateDivisionOption(form.divisionId);
  }, [isActive, divisionOptions, form.divisionId]);

  useEffect(() => {
    if (!isActive || !form.costCenterId) return;
    if (costCenterOptions.some((option) => option.value === form.costCenterId)) return;
    hydrateCostCenterOption(form.costCenterId);
  }, [isActive, costCenterOptions, form.costCenterId]);

  useEffect(() => {
    if (!isActive || selectedSkillIds.length === 0) return;
    selectedSkillIds.forEach((skillId) => {
      if (!skillOptions.some((option) => option.value === skillId)) {
        hydrateSkillOption(skillId);
      }
    });
  }, [isActive, selectedSkillIds, skillOptions]);

  function toggleSkill(skillId) {
    const nextValues = selectedSkillIds.includes(skillId)
      ? selectedSkillIds.filter((value) => value !== skillId)
      : [...selectedSkillIds, skillId];

    const nextSerialized = nextValues.join(",");
    setForm((prev) => ({ ...prev, skillId: nextSerialized }));
    if (touched.skillId) validateField("skillId", nextSerialized);
  }

  function toggleCanonicalRole(roleName) {
    const nextValues = selectedCanonicalRoleNames.includes(roleName)
      ? selectedCanonicalRoleNames.filter((v) => v !== roleName)
      : [...selectedCanonicalRoleNames, roleName];
    const nextSerialized = nextValues.join(",");
    setForm((prev) => ({ ...prev, canonicalRoleName: nextSerialized }));
    if (touched.canonicalRoleName) validateField("canonicalRoleName", nextSerialized);
  }

  function toggleRole(roleId) {
    const nextValues = selectedRoleIds.includes(roleId)
      ? selectedRoleIds.filter((value) => value !== roleId)
      : [...selectedRoleIds, roleId];
    const nextSerializedIds = nextValues.join(",");

    setForm((prev) => ({
      ...prev,
      roleId: nextSerializedIds
    }));

    if (touched.roleId) validateField("roleId", nextSerializedIds);
  }

  async function submitMutation() {
    if (!validateAll()) {
      return;
    }

    setLoading(true);
    resetFeedback();

    try {
      if (!currentAction.isAvailable) {
        throw new Error(buildOnboardingAccessMessage(i18n.t("common.currentEmployment"), currentAction.title || action));
      }

      const resolvedRoleIds = selectedRoleIds.length > 0
        ? selectedRoleIds.join(",")
        : form.roleId.trim();

      const payload = {
        empId: Number(form.empId.trim()),
        empType: form.empType || undefined,
        empStatus: form.empStatus || undefined,
        roleId: resolvedRoleIds || undefined,
        canonicalRoleName: form.canonicalRoleName.trim() || undefined,
        roleName: form.canonicalRoleName.trim() || undefined,
        skillId: form.skillId.trim() || undefined,
        skills: selectedSkillIds.length > 0 ? selectedSkillIds : (form.skillId.trim() || undefined),
        designationId: form.designationId ? Number(form.designationId) : undefined,
        departmentId: form.departmentId ? Number(form.departmentId) : undefined,
        gradeLevelId: form.gradeLevelId ? Number(form.gradeLevelId) : undefined,
        divisionId: form.divisionId ? Number(form.divisionId) : undefined,
        costCenterId: form.costCenterId ? Number(form.costCenterId) : undefined,
        workLocationId: form.workLocationId ? Number(form.workLocationId) : undefined,
        loginBasedOnGps: form.loginBasedOnGps,
        gpsLogin: form.loginBasedOnGps === "Y",
        isGpsLogin: form.loginBasedOnGps === "Y",
        managerId: form.managerId.trim() ? Number(form.managerId.trim()) : undefined,
        hrId: form.hrId.trim() ? Number(form.hrId.trim()) : undefined,
        dateOfJoin: displayDateToApiDate(form.dateOfJoin),
        terminationDate: displayDateToApiDate(form.terminationDate)
      };

      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined || payload[key] === "") delete payload[key];
      });
      console.log("pay:",JSON.stringify(payload))
      const response = await fetch(resolveApiUrl(buildUrl(currentAction.endpoint, form)), {
        method: currentAction.method,
        headers: {
          "Content-Type": "application/json",
          "x-tenant-code": tenantCode || "",
          "x-tenant-id": String(tenantId || ""),
          "x-service-code": currentAction.serviceCode,
          authorization: `Bearer ${sessionAccessToken}`
        },
        body: JSON.stringify(payload)
        
      });
      console.log("res:",response.ok)
      if (!response.ok) {
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response, getFieldLabelsMap());
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
        return;
      }

      const data = await response.json().catch(() => ({ message: i18n.t("onboarding.messages.saveCurrentEmploymentCompleted") }));
      setBanner({
        type: "success",
        msg: data.message || (action === "add"
          ? i18n.t("onboarding.messages.saveCurrentEmploymentCompleted")
          : i18n.t("onboarding.messages.actionCompletedSuccessfully", { action: i18n.t("common.update") }))
      });
      if (action === "update") {
        await fetchEmployment();
      }
      if (onSuccess) onSuccess(data);
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
    } finally {
      setLoading(false);
    }
  }

  async function runSearch() {
    setLoading(true);
    resetFeedback();

    try {
      if (!currentAction.isAvailable) {
        throw new Error(buildOnboardingAccessMessage(i18n.t("common.currentEmployment"), currentAction.title || action));
      }

      const response = await fetch(resolveApiUrl(currentAction.endpoint), {
        method: currentAction.method,
        headers: {
          "Content-Type": "application/json",
          "x-tenant-code": tenantCode || "",
          "x-tenant-id": String(tenantId || ""),
          "x-service-code": currentAction.serviceCode,
          authorization: `Bearer ${sessionAccessToken}`
        },
        body: JSON.stringify({
          empId: form.empId.trim() ? Number(form.empId.trim()) : undefined,
          empType: form.empType || undefined,
          empStatus: form.empStatus || undefined,
          canonicalRoleName: form.canonicalRoleName.trim() || undefined,
          managerId: form.managerId.trim() ? Number(form.managerId.trim()) : undefined,
          hrId: form.hrId.trim() ? Number(form.hrId.trim()) : undefined
        })
      });

      if (!response.ok) {
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response, getFieldLabelsMap());
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
        return;
      }

      await response.json();
      setBanner({ type: "success", msg: i18n.t("onboarding.messages.currentEmploymentResultsLoaded") });
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
    } finally {
      setLoading(false);
    }
  }

  async function runHierarchy() {
    if (!validateAll()) {
      return;
    }

    setLoading(true);
    resetFeedback();

    try {
      if (!currentAction.isAvailable) {
        throw new Error(buildOnboardingAccessMessage(i18n.t("common.currentEmployment"), currentAction.title || action));
      }

      const response = await fetch(resolveApiUrl(buildUrl(currentAction.endpoint, form)), {
        method: currentAction.method,
        headers: {
          "Content-Type": "application/json",
          "x-tenant-code": tenantCode || "",
          "x-tenant-id": String(tenantId || ""),
          "x-service-code": currentAction.serviceCode,
          authorization: `Bearer ${sessionAccessToken}`
        }
      });

      if (!response.ok) {
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response, getFieldLabelsMap());
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
        return;
      }

      await response.json();
      setBanner({ type: "success", msg: i18n.t("onboarding.messages.employmentHierarchyLoaded") });
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
    } finally {
      setLoading(false);
    }
  }

  function handleSave() {
    if (action === "search") {
      runSearch();
      return;
    }
    if (action === "hierarchy") {
      runHierarchy();
      return;
    }
    submitMutation();
  }

  function handleNext() {
    const nextAction = getNextAction(action);
    handleActionChange(nextAction);
    if (onNext) onNext(nextAction);
  }

  return (
    <div className={`cem-page${isTabMode ? " is-tab-mode" : ""}`}>
      <main className="cem-main">
        {!isTabMode && breadcrumb && (
          <div className="cem-breadcrumb" aria-label={i18n.t("common.breadcrumb")}>
            {breadcrumb.map((item, index) => (
              <React.Fragment key={item.label}>
                {index > 0 && <span className="cem-bc-sep">/</span>}
                <button className={`cem-bc-item${item.active ? " active" : ""}`} onClick={item.active ? undefined : item.onClick} disabled={item.active}>
                  {item.label}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        {!isTabMode && (
          <div className="cem-page-header">
            <div className="cem-page-header-row">
              <div>
                <h1 className="cem-page-title"><Trans i18nKey="common.currentEmployment" /></h1>
                <p className="cem-page-sub"><Trans i18nKey="dateTime.manageEmployeeRoleReportingJoiningDetailsAndEmployment" /></p>
              </div>
              <button className="cem-header-action" type="button" onClick={() => handleActionChange(action === "update" ? "add" : "update")}>
                {action === "update" ? i18n.t("onboarding.addNew") : i18n.t("common.update")}
              </button>
            </div>
          </div>
        )}

        <div className="cem-form-card">
          <div className="cem-section">
            <p className="cem-section-title"><Trans i18nKey="onboarding.employmentDetails" /></p>
            <div className="cem-grid">
              <Field id="empId" label={getFieldLabel("empId")} required error={touched.empId && errors.empId}>
                <input id="empId" name="empId" value={form.empId} onChange={handleInputChange} onBlur={handleInputBlur} className={`cem-input${touched.empId && errors.empId ? " error" : ""}`} placeholder={i18n.t("onboarding.enterEmployeeId")} readOnly={Boolean(initialEmployeeId)} />
              </Field>
              <Field id="empType" label={getFieldLabel("empType")} required={requiredFields.has("empType")} error={touched.empType && errors.empType}>
                <Select
                  value={form.empType}
                  onValueChange={(value) => {
                    setForm((prev) => ({ ...prev, empType: value }));
                    if (touched.empType) validateField("empType", value);
                  }}
                  onOpenChange={(open) => {
                    if (!open) {
                      const nextTouched = { ...touched, empType: true };
                      setTouched(nextTouched);
                      validateField("empType", form.empType, nextTouched);
                    }
                  }}
                >
                  <SelectTrigger id="empType" className={`cem-select${touched.empType && errors.empType ? " error" : ""}`}>
                    <SelectValue placeholder={empTypesLoading ? i18n.t("onboarding.loadingTypes") : i18n.t("onboarding.selectType")} />
                  </SelectTrigger>
                  <SelectContent>
                    {empTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>


              <Field id="empStatus" label={getFieldLabel("empStatus")} required={requiredFields.has("empStatus")} error={touched.empStatus && errors.empStatus}>
                <Select
                  value={form.empStatus}
                  onValueChange={(value) => {
                    setForm((prev) => ({ ...prev, empStatus: value }));
                    if (touched.empStatus) validateField("empStatus", value);
                  }}
                  onOpenChange={(open) => {
                    if (!open) {
                      const nextTouched = { ...touched, empStatus: true };
                      setTouched(nextTouched);
                      validateField("empStatus", form.empStatus, nextTouched);
                    }
                  }}
                >
                  <SelectTrigger id="empStatus" className={`cem-select${touched.empStatus && errors.empStatus ? " error" : ""}`}>
                    <SelectValue placeholder={empStatusesLoading ? i18n.t("onboarding.loadingStatuses") : i18n.t("onboarding.selectStatus")} />
                  </SelectTrigger>
                  <SelectContent>
                    {empStatusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>


              <Field id="roleId" label={getFieldLabel("roleId")} required>
                <div className="cem-skill-picker">
                  {rolesLoading ? (
                    <p className="cem-skill-empty">{i18n.t("onboarding.loadingRoles")}</p>
                  ) : roleOptions.length > 0 ? (
                    roleOptions.map((option) => {
                      const checked = selectedRoleIds.includes(option.value);
                      return (
                        <label key={option.value} className={`cem-skill-chip${checked ? " active" : ""}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRole(option.value)}
                            onBlur={() => {
                              const nextTouched = { ...touched, canonicalRoleName: true };
                              setTouched(nextTouched);
                              validateField("canonicalRoleName", form.canonicalRoleName, nextTouched);
                            }}
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })
                  ) : (
                    <p className="cem-skill-empty">{i18n.t("onboarding.noRolesAvailable")}</p>
                  )}
                </div>
              </Field>

              <Field id="canonicalRoleName" label={getFieldLabel("canonicalRoleName")} required={requiredFields.has("canonicalRoleName")} error={touched.canonicalRoleName && errors.canonicalRoleName} span2>
                <div className={`cem-skill-picker${touched.canonicalRoleName && errors.canonicalRoleName ? " error" : ""}`}>
                  {canonicalRolesLoading ? (
                    <p className="cem-skill-empty">{i18n.t("onboarding.loadingCanonicalRoles")}</p>
                  ) : canonicalRoleOptions.length > 0 ? (
                    canonicalRoleOptions.map((option) => {
                      const checked = selectedCanonicalRoleNames.includes(option.label);
                      return (
                        <label key={option.value} className={`cem-skill-chip${checked ? " active" : ""}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCanonicalRole(option.label)}
                            onBlur={() => {
                              const nextTouched = { ...touched, canonicalRoleName: true };
                              setTouched(nextTouched);
                              validateField("canonicalRoleName", form.canonicalRoleName, nextTouched);
                            }}
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })
                  ) : (
                    <p className="cem-skill-empty">{i18n.t("onboarding.noCanonicalRolesAvailable")}</p>
                  )}
                </div>
              </Field>

              <Field id="designationId" label={getFieldLabel("designationId")} required={requiredFields.has("designationId")} error={touched.designationId && errors.designationId}>
                {designationOptions.length > 0 ? (
                  <Select
                    value={form.designationId}
                    onValueChange={(value) => {
                      setForm((prev) => ({ ...prev, designationId: value }));
                      if (touched.designationId) validateField("designationId", value);
                    }}
                    onOpenChange={(open) => {
                      if (!open) {
                        const nextTouched = { ...touched, designationId: true };
                        setTouched(nextTouched);
                        validateField("designationId", form.designationId, nextTouched);
                      }
                    }}
                    disabled={designationsLoading || designationOptions.length === 0}
                  >
                    <SelectTrigger id="designationId" className={`cem-select${touched.designationId && errors.designationId ? " error" : ""}`}>
                      <SelectValue placeholder={designationsLoading ? i18n.t("onboarding.loadingDesignations") : designationOptions.length ? i18n.t("onboarding.selectDesignation") : i18n.t("onboarding.noDesignationsAvailable")} />
                    </SelectTrigger>
                    <SelectContent>
                      {designationOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <input id="designationId" name="designationId" value={form.designationId} onChange={handleInputChange} onBlur={handleInputBlur} className={`cem-input${touched.designationId && errors.designationId ? " error" : ""}`} placeholder={i18n.t("onboarding.enterDesignationId")} />
                )}
              </Field>

                <Field id="departmentId" label={getFieldLabel("departmentId")} required={requiredFields.has("departmentId")} error={touched.departmentId && errors.departmentId}>
                  {departmentOptions.length > 0 ? (
                    <Select
                      value={form.departmentId}
                      onValueChange={(value) => {
                        setForm((prev) => ({ ...prev, departmentId: value }));
                        if (touched.departmentId) validateField("departmentId", value);
                      }}
                      onOpenChange={(open) => {
                        if (!open) {
                          const nextTouched = { ...touched, departmentId: true };
                          setTouched(nextTouched);
                          validateField("departmentId", form.departmentId, nextTouched);
                        }
                      }}
                      disabled={departmentsLoading || departmentOptions.length === 0}
                    >
                      <SelectTrigger id="departmentId" className={`cem-select${touched.departmentId && errors.departmentId ? " error" : ""}`}>
                        <SelectValue placeholder={departmentsLoading ? i18n.t("onboarding.loadingDepartments") : departmentOptions.length ? i18n.t("onboarding.selectDepartment") : i18n.t("onboarding.noDepartmentsAvailable")} />
                      </SelectTrigger>
                      <SelectContent>
                        {departmentOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <input id="departmentId" name="departmentId" value={form.departmentId} onChange={handleInputChange} onBlur={handleInputBlur} className={`cem-input${touched.departmentId && errors.departmentId ? " error" : ""}`} placeholder={i18n.t("onboarding.enterDepartmentId")} />
                  )}
                </Field>

                <Field id="gradeLevelId" label={getFieldLabel("gradeLevelId")} required={requiredFields.has("gradeLevelId")} error={touched.gradeLevelId && errors.gradeLevelId}>
                  {gradeLevelOptions.length > 0 ? (
                    <Select
                      value={form.gradeLevelId}
                      onValueChange={(value) => {
                        setForm((prev) => ({ ...prev, gradeLevelId: value }));
                        if (touched.gradeLevelId) validateField("gradeLevelId", value);
                      }}
                      onOpenChange={(open) => {
                        if (!open) {
                          const nextTouched = { ...touched, gradeLevelId: true };
                          setTouched(nextTouched);
                          validateField("gradeLevelId", form.gradeLevelId, nextTouched);
                        }
                      }}
                      disabled={gradeLevelsLoading || gradeLevelOptions.length === 0}
                    >
                      <SelectTrigger id="gradeLevelId" className={`cem-select${touched.gradeLevelId && errors.gradeLevelId ? " error" : ""}`}>
                        <SelectValue placeholder={gradeLevelsLoading ? i18n.t("onboarding.loadingGradeLevels") : gradeLevelOptions.length ? i18n.t("onboarding.selectGradeLevel") : i18n.t("onboarding.noGradeLevelsAvailable")} />
                      </SelectTrigger>
                      <SelectContent>
                        {gradeLevelOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <input id="gradeLevelId" name="gradeLevelId" value={form.gradeLevelId} onChange={handleInputChange} onBlur={handleInputBlur} className={`cem-input${touched.gradeLevelId && errors.gradeLevelId ? " error" : ""}`} placeholder={i18n.t("onboarding.enterGradeLevelId")} />
                  )}
                </Field>

                <Field id="divisionId" label={getFieldLabel("divisionId")} required={requiredFields.has("divisionId")} error={touched.divisionId && errors.divisionId}>
                  {divisionOptions.length > 0 ? (
                    <Select
                      value={form.divisionId}
                      onValueChange={(value) => {
                        setForm((prev) => ({ ...prev, divisionId: value }));
                        if (touched.divisionId) validateField("divisionId", value);
                      }}
                      onOpenChange={(open) => {
                        if (!open) {
                          const nextTouched = { ...touched, divisionId: true };
                          setTouched(nextTouched);
                          validateField("divisionId", form.divisionId, nextTouched);
                        }
                      }}
                      disabled={divisionsLoading || divisionOptions.length === 0}
                    >
                      <SelectTrigger id="divisionId" className={`cem-select${touched.divisionId && errors.divisionId ? " error" : ""}`}>
                        <SelectValue placeholder={divisionsLoading ? i18n.t("onboarding.loadingDivisions") : divisionOptions.length ? i18n.t("onboarding.selectDivision") : i18n.t("onboarding.noDivisionsAvailable")} />
                      </SelectTrigger>
                      <SelectContent>
                        {divisionOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <input id="divisionId" name="divisionId" value={form.divisionId} onChange={handleInputChange} onBlur={handleInputBlur} className={`cem-input${touched.divisionId && errors.divisionId ? " error" : ""}`} placeholder={i18n.t("onboarding.enterDivisionId")} />
                  )}
                </Field>

                <Field id="costCenterId" label={getFieldLabel("costCenterId")} required={requiredFields.has("costCenterId")} error={touched.costCenterId && errors.costCenterId}>
                  {costCenterOptions.length > 0 ? (
                    <Select
                      value={form.costCenterId}
                      onValueChange={(value) => {
                        setForm((prev) => ({ ...prev, costCenterId: value }));
                        if (touched.costCenterId) validateField("costCenterId", value);
                      }}
                      onOpenChange={(open) => {
                        if (!open) {
                          const nextTouched = { ...touched, costCenterId: true };
                          setTouched(nextTouched);
                          validateField("costCenterId", form.costCenterId, nextTouched);
                        }
                      }}
                      disabled={costCentersLoading || costCenterOptions.length === 0}
                    >
                      <SelectTrigger id="costCenterId" className={`cem-select${touched.costCenterId && errors.costCenterId ? " error" : ""}`}>
                        <SelectValue placeholder={costCentersLoading ? i18n.t("onboarding.loadingCostCenters") : costCenterOptions.length ? i18n.t("onboarding.selectCostCenter") : i18n.t("onboarding.noCostCentersAvailable")} />
                      </SelectTrigger>
                      <SelectContent>
                        {costCenterOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <input id="costCenterId" name="costCenterId" value={form.costCenterId} onChange={handleInputChange} onBlur={handleInputBlur} className={`cem-input${touched.costCenterId && errors.costCenterId ? " error" : ""}`} placeholder={i18n.t("onboarding.enterCostCenterId")} />
                  )}
                </Field>


              <Field id="workLocationId" label={getFieldLabel("workLocationId")} required={requiredFields.has("workLocationId")} error={touched.workLocationId && errors.workLocationId}>
                <Select
                  value={form.workLocationId}
                  onValueChange={(value) => {
                    setForm((prev) => ({ ...prev, workLocationId: value }));
                    if (touched.workLocationId) validateField("workLocationId", value);
                  }}
                  onOpenChange={(open) => {
                    if (!open) {
                      const nextTouched = { ...touched, workLocationId: true };
                      setTouched(nextTouched);
                      validateField("workLocationId", form.workLocationId, nextTouched);
                    }
                  }}
                >
                  <SelectTrigger
                    id="workLocationId"
                    className={`cem-select${touched.workLocationId && errors.workLocationId ? " error" : ""}`}
                  >
                    <SelectValue placeholder={i18n.t("common.selectLocation")} />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{i18n.t(option.labelKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>


                <Field id="dateOfJoin" label={getFieldLabel("dateOfJoin")} required={requiredFields.has("dateOfJoin")} error={touched.dateOfJoin && errors.dateOfJoin}>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={`cem-input${touched.dateOfJoin && errors.dateOfJoin ? " error" : ""} w-full h-auto justify-start text-left font-normal`}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-[var(--color-text-secondary)]" />
                        {form.dateOfJoin ? form.dateOfJoin : <span className="text-muted-foreground">{i18n.t("onboarding.ddMmYyyy")}</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                      <Calendar
                        mode="single"
                        captionLayout="dropdown"
                        startMonth={new Date(1990, 0)}
                        endMonth={new Date(2100, 0)}
                        selected={/^\d{2}-\d{2}-\d{4}$/.test(form.dateOfJoin) ? parse(form.dateOfJoin, "dd-MM-yyyy", new Date()) : undefined}
                        onSelect={(date) => {
                          if (!date) return;
                          const v = format(date, "dd-MM-yyyy");
                          setForm((prev) => ({ ...prev, dateOfJoin: v }));
                          const nextTouched = { ...touched, dateOfJoin: true };
                          setTouched(nextTouched);
                          validateField("dateOfJoin", v, nextTouched);
                          if (form.terminationDate) validateField("terminationDate", form.terminationDate, nextTouched);
                        }}
                        initialFocus
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </Field>
                <Field id="terminationDate" label={getFieldLabel("terminationDate")} error={touched.terminationDate && errors.terminationDate}>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={`cem-input${touched.terminationDate && errors.terminationDate ? " error" : ""} w-full h-auto justify-start text-left font-normal`}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-[var(--color-text-secondary)]" />
                        {form.terminationDate ? form.terminationDate : <span className="text-muted-foreground">{i18n.t("onboarding.ddMmYyyy")}</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                      <Calendar
                        mode="single"
                        captionLayout="dropdown"
                        startMonth={new Date(1990, 0)}
                        endMonth={new Date(2100, 0)}
                        selected={/^\d{2}-\d{2}-\d{4}$/.test(form.terminationDate) ? parse(form.terminationDate, "dd-MM-yyyy", new Date()) : undefined}
                        disabled={
                          /^\d{2}-\d{2}-\d{4}$/.test(form.dateOfJoin)
                            ? { before: parse(form.dateOfJoin, "dd-MM-yyyy", new Date()) }
                            : undefined
                        }
                        onSelect={(date) => {
                          if (!date) return;
                          const v = format(date, "dd-MM-yyyy");
                          setForm((prev) => ({ ...prev, terminationDate: v }));
                          const nextTouched = { ...touched, terminationDate: true };
                          setTouched(nextTouched);
                          validateField("terminationDate", v, nextTouched);
                        }}
                        initialFocus
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </Field>
              <Field id="managerId" label={getFieldLabel("managerId")} required error={touched.managerId && errors.managerId}>
                <input id="managerId" name="managerId" value={form.managerId} onChange={handleInputChange} onBlur={handleInputBlur} className={`cem-input${touched.managerId && errors.managerId ? " error" : ""}`} placeholder={i18n.t("formActions.searchManager")} />
              </Field>
              <Field id="hrId" label={getFieldLabel("hrId")} required error={touched.hrId && errors.hrId}>
                <input id="hrId" name="hrId" value={form.hrId} onChange={handleInputChange} onBlur={handleInputBlur} className={`cem-input${touched.hrId && errors.hrId ? " error" : ""}`} placeholder={i18n.t("formActions.searchHr")} />
              </Field>
              <Field id="skillId" label={getFieldLabel("skillId")} required error={touched.skillId && errors.skillId} span2>
                {skillOptions.length > 0 ? (
                  <div className={`cem-skill-picker${touched.skillId && errors.skillId ? " error" : ""}`}>
                    {skillsLoading ? (
                      <p className="cem-skill-empty">{i18n.t("onboarding.loadingSkills")}</p>
                    ) : (
                      skillOptions.map((option) => {
                        const checked = selectedSkillIds.includes(option.value);
                        return (
                          <label key={option.value} className={`cem-skill-chip${checked ? " active" : ""}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSkill(option.value)}
                              onBlur={() => {
                                const nextTouched = { ...touched, skillId: true };
                                setTouched(nextTouched);
                                validateField("skillId", form.skillId, nextTouched);
                              }}
                            />
                            <span>{option.label}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                ) : (
                  <input id="skillId" name="skillId" value={form.skillId} onChange={handleInputChange} onBlur={handleInputBlur} className={`cem-input${touched.skillId && errors.skillId ? " error" : ""}`} placeholder={i18n.t("onboarding.enterSkillIdsCommaSeparated")} />
                )}
              </Field>
              <Field id="gpsToggle" label={i18n.t("onboarding.labels.gpsLogin")} span2>
                <div className="cem-toggle-wrap">
                  <button type="button" className={`cem-toggle${form.loginBasedOnGps === "Y" ? " active" : ""}`} onClick={() => setForm((prev) => ({ ...prev, loginBasedOnGps: prev.loginBasedOnGps === "Y" ? "N" : "Y" }))}>
                    <span className="cem-toggle-knob" />
                  </button>
                  <span className="cem-toggle-text">{form.loginBasedOnGps === "Y" ? i18n.t("common.yes") : i18n.t("common.no")}</span>
                </div>
              </Field>
            </div>
          </div>

          <div className="cem-section">
            {banner && <div className={`cem-banner ${banner.type}`}>{banner.msg}</div>}
            {!isTabMode && (
              <div className="cem-actions">
                <button className="cem-secondary-btn" type="button" onClick={onBack}>{i18n.t("formActions.back")}</button>
                <button className="cem-primary-btn" type="button" onClick={handleSave} disabled={loading || !currentAction.isAvailable}>
                  {loading
                    ? i18n.t("localConveyance.saving")
                    : action === "search"
                      ? i18n.t("common.search")
                      : action === "hierarchy"
                        ? i18n.t("onboarding.getHierarchy")
                        : i18n.t("formActions.save")}
                </button>
                <button className="cem-secondary-btn" type="button" onClick={handleNext}>{i18n.t("pagination.next2")}</button>
              </div>
            )}
          </div>
        </div>

      </main>
    </div>
  );
}
